import { useId } from 'react'
import { motion } from 'framer-motion'

/**
 * TourSpotlight — full-screen dimmed backdrop with a softly cut-out
 * window around the target element. Rendered as an SVG with a mask so
 * the cutout edges can be properly feathered.
 *
 * Target rect is computed by the parent FirstLoginTour via
 * ResizeObserver + scroll listener; the spotlight itself is "dumb" and
 * just draws what it's told.
 */
interface TourSpotlightProps {
  /** Target rect in viewport coordinates. When null, draws a solid backdrop. */
  rect: DOMRect | null
  /** Padding around the target. Defaults to 8px. */
  padding?: number
  /** Corner radius of the cutout. Defaults to 14px. */
  radius?: number
  /** Backdrop opacity. Defaults to 0.82. */
  opacity?: number
}

export default function TourSpotlight({
  rect,
  padding = 10,
  radius = 16,
  opacity = 0.82,
}: TourSpotlightProps) {
  const maskId = useId()
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0

  const r = rect
    ? {
        x: Math.max(0, rect.left - padding),
        y: Math.max(0, rect.top - padding),
        w: rect.width + padding * 2,
        h: rect.height + padding * 2,
      }
    : null

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={`0 0 ${vw} ${vh}`}
      width={vw}
      height={vh}
      aria-hidden="true"
    >
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse">
          <rect x={0} y={0} width={vw} height={vh} fill="white" />
          {r && (
            <motion.rect
              layoutId="tour-spotlight-cutout"
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              rx={radius}
              ry={radius}
              fill="black"
              transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            />
          )}
        </mask>
      </defs>
      <rect
        x={0}
        y={0}
        width={vw}
        height={vh}
        fill="rgba(0,0,0,1)"
        opacity={opacity}
        mask={`url(#${maskId})`}
      />
      {r && (
        <motion.rect
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.h}
          rx={radius}
          ry={radius}
          fill="none"
          stroke="var(--bn-accent)"
          strokeOpacity="0.55"
          strokeWidth="1.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.12, duration: 0.3 }}
        />
      )}
    </svg>
  )
}
